import Divider from '@/components/divider';
import ImageOptimizer from '@/components/image-optimizer';
import { Button } from '@/components/ui/button';
import {
  Carousel,
  CarouselApi,
  CarouselContent,
  CarouselItem,
} from '@/components/ui/carousel';
import Autoplay from 'embla-carousel-autoplay';
import React, { useEffect, useState } from 'react';

interface Instructor {
  id: string;
  name: string;
  position: string;
  intro?: string;
  isAcademicInstructorCertified?: number;
  isPracticalInstructorCertified?: number;
  isExaminerCertified?: number;
  licenses?: string;
}

interface EducatorProfileProps {
  instructors: Instructor[];
  /** 위쪽 구분선·여백 표시(기본 true). 바로 위 영역이 이미 경계를 그리면 끈다. */
  showTopDivider?: boolean;
}

const EducatorProfile: React.FC<EducatorProfileProps> = ({
  instructors,
  showTopDivider = true,
}) => {
  const [pcApi, setPcApi] = useState<CarouselApi>();
  const [pcCurrentSlide, setPcCurrentSlide] = useState(0);

  // 강사 정보가 없으면 섹션 전체를 숨김
  if (!instructors || instructors.length === 0) {
    return null;
  }

  // const instructorMultipleList = Array.isArray(instructors)
  //   ? [...instructors, ...instructors]
  //   : [];

  useEffect(() => {
    if (!pcApi) return;

    const onPcSelect = () => {
      setPcCurrentSlide(pcApi.selectedScrollSnap());
    };
    pcApi.on('select', onPcSelect);
    onPcSelect();

    return () => {
      pcApi.off('select', onPcSelect);
    };
  }, [pcApi]);

  const handleNextClick = () => {
    pcApi?.scrollNext();
    const autoplay = pcApi?.plugins().autoplay;
    if (!autoplay) return;
    autoplay.stop();
    setTimeout(() => autoplay.play(), 1000);
  };

  const handlePrevClick = () => {
    pcApi?.scrollPrev();
    const autoplay = pcApi?.plugins().autoplay;
    if (!autoplay) return;
    autoplay.stop();
    setTimeout(() => autoplay.play(), 1000);
  };
  const renderInstructorCard = (inst: Instructor) => {
    const certificates = [
      inst.isAcademicInstructorCertified ? '학과교육강사' : null,
      inst.isPracticalInstructorCertified ? '기능교육강사' : null,
      inst.isExaminerCertified ? '기능검정원' : null,
    ].filter(Boolean) as string[];

    const licenses = inst.licenses
      ? inst.licenses
          .split('\n')
          .map((l) => l.trim())
          .filter(Boolean)
      : [];

    return (
      <>
        <div className="flex h-auto flex-col gap-4 rounded-xl bg-gray-50 px-4 py-5">
          <div className="flex items-center gap-1">
            <ImageOptimizer
              src="/images/icons/icon_person.svg"
              alt="icon"
              width={20}
              height={20}
            />
            <span className="typo-18 text-level-1 font-bold">
              {inst.name} {inst.position}
            </span>
          </div>
          {inst.intro && inst.intro.trim() && (
            <div className="typo-16 font-bold text-orange-500">
              "{inst.intro}"
            </div>
          )}
          <div>
            {certificates.length > 0 && (
              <>
                <div className="typo-16 text-level-3 mb-2 font-bold">
                  국가공인 자격증 취득현황
                </div>
                <div className="mb-4 flex flex-wrap gap-2">
                  {certificates.map((cert) => (
                    <span
                      key={cert}
                      className="text-level-4 typo-16 rounded-lg bg-white px-3 py-[10px]"
                    >
                      {cert}
                    </span>
                  ))}
                </div>
              </>
            )}
            {licenses.length > 0 && (
              <>
                <div className="typo-16 text-level-3 mb-2 font-bold">
                  보유면허
                </div>
                <div className="flex flex-wrap gap-2">
                  {licenses.map((license) => (
                    <span
                      key={license}
                      className="text-level-4 typo-16 rounded-lg bg-white px-3 py-[10px]"
                    >
                      {license}
                    </span>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      </>
    );
  };

  return (
    <>
      {instructors.length > 0 && showTopDivider && (
        <>
          <Divider className="space-y-11" />
          <div className="desktop:pt-9 pt-8" />
        </>
      )}
      {/* PC 버전 - 캐러셀 */}
      <div className="desktop:block hidden">
        <div className="flex items-center justify-between">
          <h3 className="desktop:typo-24 typo-22 text-level-2 mb-5 font-extrabold">
            강사안내
          </h3>
          {instructors.length > 1 && (
            <div className="mb-3">
              <Button
                onClick={handlePrevClick}
                disabled={!pcApi}
                className="px-1 shadow-none"
              >
                <ImageOptimizer
                  src="/images/icons/icon_slide_left_white.webp"
                  alt="다음 슬라이드"
                  width={24}
                  height={24}
                  className="cursor-pointer rounded-full hover:bg-gray-100"
                />
              </Button>
              <Button
                onClick={handleNextClick}
                disabled={!pcApi}
                className="px-1 shadow-none"
              >
                <ImageOptimizer
                  src="/images/icons/icon_slide_right_white.webp"
                  alt="다음 슬라이드"
                  width={24}
                  height={24}
                  className="cursor-pointer rounded-full hover:bg-gray-100"
                />
              </Button>
            </div>
          )}
        </div>

        <div className="relative">
          <Carousel
            setApi={setPcApi}
            opts={{
              align: 'start',
              loop: true,
              slidesToScroll: 1,
            }}
            plugins={[
              Autoplay({
                delay: 3000,
                stopOnMouseEnter: true,
                stopOnInteraction: false,
              }),
            ]}
          >
            <CarouselContent className="mx-auto -ml-4">
              {instructors.map((inst, idx) => (
                <CarouselItem
                  key={inst.id + inst.name + idx}
                  className={`w-[400px] flex-shrink-0 basis-[400px] ${
                    idx === pcCurrentSlide ||
                    idx === (pcCurrentSlide + 1) % instructors.length
                      ? 'opacity-100'
                      : 'opacity-50'
                  }`}
                >
                  {renderInstructorCard(inst)}
                </CarouselItem>
              ))}
            </CarouselContent>
          </Carousel>
        </div>
      </div>

      {/* 모바일 버전 - 수직 정렬 */}
      <div className="desktop:hidden flex flex-col gap-4">
        {instructors.map((inst, idx) => (
          <div key={inst.id + inst.name + idx}>
            {renderInstructorCard(inst)}
          </div>
        ))}
      </div>
    </>
  );
};

export default EducatorProfile;
